import api from "./axios";

/**
 * EL CLIENTE DE LOS MÓDULOS NUEVOS.
 *
 * Un solo archivo con todas las llamadas, agrupadas por módulo. La alternativa
 * —cada pantalla armando su `api.get("/...")`— reparte las rutas por veinte
 * componentes, y basta con que el backend renombre una para tener que buscarla
 * a ciegas.
 *
 * Todas devuelven `res.data` ya desempaquetado: quien las usa quiere los
 * datos, no la respuesta de axios.
 */

const datos = (p) => p.then((r) => r.data);

/** Catálogos y qué módulos tiene encendidos la residencial. */
export const catalogos = () => datos(api.get("/catalogos"));

// ── Fotografías ───────────────────────────────────────────────────────
export const fotos = {
  /**
   * Sube una imagen en tres pasos: firmar, PUT directo a S3, confirmar.
   *
   * El archivo NUNCA pasa por el servidor. Y se comprime ANTES de subir
   * —ver `utils/imagen.js`—: una foto de teléfono son 4 MB y en una caseta
   * con mala señal eso es medio minuto de espera con alguien delante.
   */
  async subir(archivo, { ambito, recursoId, alProgresar }) {
    const { comprimirImagen } = await import("../utils/imagen");
    const comprimida = await comprimirImagen(archivo);

    const { key, url } = await datos(
      api.post("/media/firmar", {
        contentType: comprimida.type,
        ambito,
        recursoId,
        size: comprimida.size,
      })
    );

    // Con `fetch` y no con axios: el PUT va a S3, y el interceptor de cifrado
    // de axios convertiría el cuerpo en un sobre que S3 no entiende.
    const respuesta = await fetch(url, {
      method: "PUT",
      body: comprimida,
      headers: { "Content-Type": comprimida.type },
    });
    if (!respuesta.ok) throw new Error("No se pudo subir la imagen");

    if (alProgresar) alProgresar(1);
    return datos(api.post("/media/confirmar", { key }));
  },

  urlDeLectura: (key) => datos(api.get("/media/url", { params: { key } })),
};

/**
 * Archivos que NO son fotos: el reglamento, un acta, un estado de cuenta.
 *
 * Mismo recorrido de tres pasos, pero **sin comprimir**. Pasar un PDF por el
 * compresor de imágenes no da error: devuelve un archivo roto, con el tipo
 * cambiado, que se sube tan campante y sólo se descubre cuando alguien
 * intenta abrirlo semanas después. Por eso es una función aparte y no un
 * parámetro de la otra.
 *
 * El backend acepta PDF únicamente en el ámbito `documentos`.
 */
export const archivos = {
  async subir(archivo, { ambito = "documentos", recursoId }) {
    const { key, url } = await datos(
      api.post("/media/firmar", {
        contentType: archivo.type,
        ambito,
        recursoId,
        size: archivo.size,
      })
    );

    const respuesta = await fetch(url, {
      method: "PUT",
      body: archivo,
      headers: { "Content-Type": archivo.type },
    });
    if (!respuesta.ok) throw new Error("No se pudo subir el archivo");

    const confirmado = await datos(api.post("/media/confirmar", { key }));
    return { ...confirmado, key, originalName: archivo.name, contentType: archivo.type, size: archivo.size };
  },
};

// ── Puntos de acceso ──────────────────────────────────────────────────
export const puntosAcceso = {
  listar: (params) => datos(api.get("/access-points", { params })),
  crear: (cuerpo) => datos(api.post("/access-points", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/access-points/${id}`, cuerpo)),
  desactivar: (id) => datos(api.delete(`/access-points/${id}`)),
};

// ── Vehículos ─────────────────────────────────────────────────────────
export const vehiculos = {
  listar: (params) => datos(api.get("/vehicles", { params })),
  porPlaca: (placa) => datos(api.get(`/vehicles/placa/${encodeURIComponent(placa)}`)),
  crear: (cuerpo) => datos(api.post("/vehicles", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/vehicles/${id}`, cuerpo)),
  darDeBaja: (id) => datos(api.delete(`/vehicles/${id}`)),
};

// ── Visitantes recurrentes ────────────────────────────────────────────
export const recurrentes = {
  listar: (params) => datos(api.get("/recurring-visitors", { params })),
  frecuentes: () => datos(api.get("/recurring-visitors/frecuentes")),
  crear: (cuerpo) => datos(api.post("/recurring-visitors", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/recurring-visitors/${id}`, cuerpo)),
  revocar: (id) => datos(api.delete(`/recurring-visitors/${id}`)),
  registrarEntrada: (id, cuerpo) => datos(api.post(`/visits/recurrente/${id}/entrada`, cuerpo)),
};

// ── Restricciones ─────────────────────────────────────────────────────
export const restricciones = {
  listar: (params) => datos(api.get("/restrictions", { params })),
  comprobar: (params) => datos(api.get("/restrictions/comprobar", { params })),
  reportar: (cuerpo) => datos(api.post("/restrictions", cuerpo)),
  aprobar: (id, cuerpo) => datos(api.patch(`/restrictions/${id}/aprobar`, cuerpo)),
  levantar: (id, cuerpo) => datos(api.patch(`/restrictions/${id}/levantar`, cuerpo)),
};

// ── Incidentes ────────────────────────────────────────────────────────
export const incidentes = {
  listar: (params) => datos(api.get("/incidents", { params })),
  obtener: (id) => datos(api.get(`/incidents/${id}`)),
  crear: (cuerpo) => datos(api.post("/incidents", cuerpo)),
  comentar: (id, cuerpo) => datos(api.post(`/incidents/${id}/comentarios`, cuerpo)),
  cerrar: (id, cuerpo) => datos(api.patch(`/incidents/${id}/cerrar`, cuerpo)),
};

// ── Turnos ────────────────────────────────────────────────────────────
export const turnos = {
  listar: (params) => datos(api.get("/shifts", { params })),
  crear: (cuerpo) => datos(api.post("/shifts", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/shifts/${id}`, cuerpo)),
  eliminar: (id) => datos(api.delete(`/shifts/${id}`)),
  marcarEntrada: (id) => datos(api.patch(`/shifts/${id}/entrada`)),
  marcarSalida: (id) => datos(api.patch(`/shifts/${id}/salida`)),
};

// ── Comunicados ───────────────────────────────────────────────────────
export const comunicados = {
  listar: (params) => datos(api.get("/announcements", { params })),
  crear: (cuerpo) => datos(api.post("/announcements", cuerpo)),
  marcarLeido: (id) => datos(api.patch(`/announcements/${id}/leido`)),
  archivar: (id) => datos(api.patch(`/announcements/${id}/archivar`)),
  lecturas: (id) => datos(api.get(`/announcements/${id}/lecturas`)),
};

// ── Amenidades y reservas ─────────────────────────────────────────────
export const amenidades = {
  listar: (params) => datos(api.get("/amenities", { params })),
  crear: (cuerpo) => datos(api.post("/amenities", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/amenities/${id}`, cuerpo)),
};

export const reservas = {
  listar: (params) => datos(api.get("/reservations", { params })),
  crear: (cuerpo) => datos(api.post("/reservations", cuerpo)),
  decidir: (id, cuerpo) => datos(api.patch(`/reservations/${id}/decidir`, cuerpo)),
  cancelar: (id) => datos(api.patch(`/reservations/${id}/cancelar`)),
};

// ── Mantenimiento ─────────────────────────────────────────────────────
export const mantenimiento = {
  listar: (params) => datos(api.get("/maintenance", { params })),
  crear: (cuerpo) => datos(api.post("/maintenance", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.patch(`/maintenance/${id}`, cuerpo)),
};

// ── Paquetería ────────────────────────────────────────────────────────
export const paquetes = {
  listar: (params) => datos(api.get("/packages", { params })),
  recibir: (cuerpo) => datos(api.post("/packages", cuerpo)),
  entregar: (id, cuerpo) => datos(api.patch(`/packages/${id}/entregar`, cuerpo)),
};

// ── Eventos ───────────────────────────────────────────────────────────
export const eventos = {
  listar: (params) => datos(api.get("/events", { params })),
  invitados: (id) => datos(api.get(`/events/${id}/invitados`)),
  crear: (cuerpo) => datos(api.post("/events", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/events/${id}`, cuerpo)),
};

// ── Botón de emergencia ───────────────────────────────────────────────
export const sos = {
  activar: (cuerpo) => datos(api.post("/sos", cuerpo || {})),
  listar: (params) => datos(api.get("/sos", { params })),
  atender: (id) => datos(api.patch(`/sos/${id}/atender`)),
  cerrar: (id, cuerpo) => datos(api.patch(`/sos/${id}/cerrar`, cuerpo)),
};

// ── Encuestas ─────────────────────────────────────────────────────────
export const encuestas = {
  listar: (params) => datos(api.get("/surveys", { params })),
  crear: (cuerpo) => datos(api.post("/surveys", cuerpo)),
  votar: (id, cuerpo) => datos(api.post(`/surveys/${id}/votar`, cuerpo)),
  cerrar: (id) => datos(api.patch(`/surveys/${id}/cerrar`)),
};

// ── Documentos de la residencial ──────────────────────────────────────
export const documentos = {
  listar: (params) => datos(api.get("/documents", { params })),
  crear: (cuerpo) => datos(api.post("/documents", cuerpo)),
  archivar: (id) => datos(api.patch(`/documents/${id}/archivar`)),
};

// ── Junta directiva ───────────────────────────────────────────────────
export const reuniones = {
  listar: (params) => datos(api.get("/meetings", { params })),
  crear: (cuerpo) => datos(api.post("/meetings", cuerpo)),
  levantarActa: (id, cuerpo) => datos(api.patch(`/meetings/${id}/acta`, cuerpo)),
};

// ── Proveedores ───────────────────────────────────────────────────────
export const proveedores = {
  listar: (params) => datos(api.get("/suppliers", { params })),
  crear: (cuerpo) => datos(api.post("/suppliers", cuerpo)),
  actualizar: (id, cuerpo) => datos(api.put(`/suppliers/${id}`, cuerpo)),
};

// ── Entregas ──────────────────────────────────────────────────────────
export const entregas = {
  verPreferencia: (params) => datos(api.get("/deliveries/preferencia", { params })),
  guardarPreferencia: (cuerpo) => datos(api.put("/deliveries/preferencia", cuerpo)),
  /** Lo que la caseta consulta con el repartidor delante. */
  consultar: (params) => datos(api.get("/deliveries/consultar", { params })),
  listar: () => datos(api.get("/deliveries")),
};

/**
 * Usuarios, para lo que los módulos nuevos necesitan de ellos.
 *
 * `AdminDashboard` sigue hablando con `/users` por su cuenta; esto no lo
 * sustituye. Sólo agrupa lo que usan las pantallas nuevas —la ficha del
 * guardia— para no repartir la ruta por más componentes.
 */
export const usuarios = {
  listar: (params) => datos(api.get("/users", { params })),
  fichaGuardia: (id, cuerpo) => datos(api.patch(`/users/${id}/ficha-guardia`, cuerpo)),
};

// ── Paneles ───────────────────────────────────────────────────────────
export const panel = {
  centroDeSeguridad: () => datos(api.get("/panel/seguridad")),
  general: () => datos(api.get("/panel/general")),
  estadisticas: (params) => datos(api.get("/panel/estadisticas", { params })),
  bitacora: (params) => datos(api.get("/panel/bitacora", { params })),
  directorio: () => datos(api.get("/panel/directorio")),
  buscar: (q) => datos(api.get("/panel/buscar", { params: { q } })),
  historialPersona: (nombre) => datos(api.get(`/panel/persona/${encodeURIComponent(nombre)}`)),
  historialCasa: (poligono, casa) =>
    datos(api.get(`/panel/casa/${encodeURIComponent(poligono)}/${encodeURIComponent(casa)}`)),
};

// ── Visitas: lo que se añadió ─────────────────────────────────────────
export const visitas = {
  solicitarAutorizacion: (cuerpo) => datos(api.post("/visits/solicitar-autorizacion", cuerpo)),
  decidir: (id, cuerpo) => datos(api.patch(`/visits/${id}/decidir`, cuerpo)),
  revocar: (id, cuerpo) => datos(api.patch(`/visits/${id}/revocar`, cuerpo)),
  adjuntarFotos: (id, cuerpo) => datos(api.post(`/visits/${id}/fotos`, cuerpo)),
};
